import React from "react";
import PropTypes from "prop-types";
import { Field, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { Col } from "react-bootstrap";
import * as actions from "MODULES/Lots/actions";
import { DashedSeparator } from "MODULES/shared/components";
import DoNotDoBusinessWarning from "MODULES/companies/shared/components/DoNotDoBusinessWarning";
import ShippingFields from "MODULES/Orders/shared/ShippingFields";
import CompanyAddressWarning from "MODULES/shared/components/CompanyAddressWarning";
import ActiveDemeritCompanyWarning from "MODULES/companies/shared/components/ActiveDemeritCompanyWarning";
import {
  form,
  Form as AppForm,
  FormInput,
  Select,
  FormRow,
  Check,
  CheckGroup
} from "MODULES/shared/components/Forms";
import {
  COST,
  NAME,
  REFERENCE_NUMBER,
  SHIP_TO_COMPANY_ADDRESS_ID,
  SHIP_TO_COMPANY_ID,
  SHIPPING_ADDRESS,
  SHIPPING_METHOD,
  SHIPPING_TYPE
} from "APP_ROOT/constants";
import {
  COST_SPLIT,
  COST_SPLIT_LABEL,
  LOT_NAME,
  LOT_PRICE,
  LOT_TYPE,
  LOT_TYPES,
  SHIP_DATE,
  AS_IS
} from "MODULES/Lots/constants";
import { INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import { properCase } from "LIB/formatters/text";
import {
  CONDITION,
  LEAD_TIME,
  TRACE_TYPE,
  TRACED_TO
} from "MODULES/inventories/constants";
import { toOptions } from "LIB/forms";
import { CONDITION_TYPES } from "MODULES/inventories/components/Detail/Dummies/constants";
import DetailFields from "MODULES/Lots/shared/DetailFields/DetailFields";
import NumberInput from "MODULES/shared/components/Forms/NumberInput/NumberInput";
import Warning from "MODULES/shared/components/Warning/Warning";
import validate from "./validate";

const formName = "createLotOrder";
const selector = formValueSelector(formName);

export class Form extends React.Component {
  constructor() {
    super();
    this.notifyForm = this.notifyForm.bind(this);
  }

  state = {
    invalidCompany: false,
    company: {}
  };

  notifyForm = company => {
    if (
      company &&
      (!company.primary_physical_address || !company.billing_address)
    ) {
      return this.setState({ invalidCompany: true, company });
    }
    return this.setState({ invalidCompany: false });
  };

  render() {
    const { setSelectedCompany, selectedCompany, costSplit } = this.props;
    const { invalidCompany, company } = this.state;

    const shouldShowWarning = !(parseInt(costSplit, 10) > 0);

    return (
      <div>
        <AppForm {...this.props} disabled={invalidCompany}>
          <AppForm.Body>
            <DoNotDoBusinessWarning company={selectedCompany} />
            {invalidCompany && <CompanyAddressWarning company={company} />}
            <ActiveDemeritCompanyWarning company={selectedCompany} />
            <DetailFields
              notifyForm={this.notifyForm}
              setPaymentTerms={this.setPaymentTerms}
              selectedBuyer={this.props.selectedBuyer}
              formName={formName}
              setSelectedCompany={setSelectedCompany}
            />
            <FormRow>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={LOT_TYPE}
                    component={Select}
                    options={toOptions(LOT_TYPES)}
                    label={properCase(LOT_TYPE)}
                    required
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={NAME}
                    component={FormInput}
                    label={properCase(LOT_NAME)}
                    required
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={REFERENCE_NUMBER}
                    component={FormInput}
                    label="Ref #"
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={COST}
                    component={NumberInput}
                    label={properCase(LOT_PRICE)}
                    required
                  />
                </div>
              </Col>
            </FormRow>
            <FormRow>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={CONDITION}
                    component={Select}
                    options={toOptions(CONDITION_TYPES)}
                    label={properCase(CONDITION)}
                  />
                </div>
              </Col>
              <Col md={2}>
                <CheckGroup title="As Is">
                  <Field name={AS_IS} component={Check} label="As-is" />
                </CheckGroup>
              </Col>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    name={LEAD_TIME}
                    component={FormInput}
                    label="Lead Time (Days)"
                    type="number"
                    inputProps={{
                      min: 0
                    }}
                    required
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="form-group">
                  <Field
                    label={COST_SPLIT_LABEL}
                    name={COST_SPLIT}
                    component={NumberInput}
                    roundDigits={0}
                    type="number"
                  />
                </div>
              </Col>
            </FormRow>
            <FormRow>
              <Col md={3}>
                <Field
                  name={TRACE_TYPE}
                  component={Select}
                  options={toOptions(INCOMING_LINE_ITEMS.traceTypes)}
                  label={properCase(TRACE_TYPE)}
                />
              </Col>
              <Col md={3}>
                <Field
                  name={TRACED_TO}
                  component={FormInput}
                  label="Traced To"
                />
              </Col>
            </FormRow>
            <DashedSeparator />
            <h2>Shipping</h2>
            <FormRow>
              <Col md={6}>
                <div className="form-group">
                  <Field
                    name="fob"
                    component={FormInput}
                    label="FOB"
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <Field
                    name={SHIP_DATE}
                    component={FormInput}
                    type="date"
                    label={properCase(SHIP_DATE)}
                  />
                </div>
              </Col>
            </FormRow>
            <DashedSeparator />
            <ShippingFields
              formName={formName}
              path={SHIPPING_ADDRESS}
              shippingFields={[
                SHIPPING_TYPE,
                SHIP_TO_COMPANY_ID,
                SHIP_TO_COMPANY_ADDRESS_ID
              ]}
              shippingMethod={SHIPPING_METHOD}
              shippingAddressField={SHIPPING_ADDRESS}
              setTenantAccount
            />
            <div>
              {shouldShowWarning && (
                <Warning>
                  <p>
                    Please confirm that this Lot PO does not have an applied
                    cost percentage before moving forward.
                  </p>
                </Warning>
              )}
            </div>
          </AppForm.Body>
        </AppForm>
      </div>
    );
  }
}

Form.propTypes = {
  selectedBuyer: PropTypes.instanceOf(Object),
  selectedCompany: PropTypes.instanceOf(Object).isRequired,
  setSelectedCompany: PropTypes.func.isRequired,
  costSplit: PropTypes.number
};

export default connect(
  state => ({
    costSplit: selector(state, COST_SPLIT)
  }),
  dispatch => ({
    onSubmit: data => dispatch(actions.create(data))
  })
)(
  form({
    form: formName,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/Lots/Create/Form.jsx